import { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { getMe } from "lib/reducers/lookupData.selectors";
import { useDispatch, useSelector } from "store";
import { fetchAssets, fetchStorageById, selectAssetsListByStorageId, selectStorageByID } from "store/reducers";
import { PrimaryButton, SecondaryButton } from "components/Buttons";
import { useIsAdmin } from "components/Restricted";
import {
  assetLabel,
  deriveOverallStatus,
  SensorBoardPanel,
  SensorDetailModal,
  SensorHealthEmptyState,
  SensorHealthOverview,
  SensorKey,
  SensorToolSearch,
  TriageBucket,
  useSensorHealthCheck,
} from "components/shared/features/storages/sensorHealth";

/**
 * SensorHealthPage is the full-page, admin-only live sensor-health screen for a toolbox. It opens in
 * a new tab from the toolbox settings page and boots independently of the rest of the app shell.
 */
export const SensorHealthPage = () => {
  const dispatch = useDispatch();
  const { storageID = "" } = useParams<{ storageID: string }>();
  const isAdmin = useIsAdmin();
  const storage = useSelector(selectStorageByID(storageID));
  const assets = useSelector(selectAssetsListByStorageId(storageID));

  // Fresh tab: redux starts empty and the admin check's lookups arrive async — hold rather than
  // bounce a legitimate admin to /restricted (see DeviceShellPage for the same guard).
  const me = useSelector(getMe);
  const roles = useSelector((state) => state.lookupData.roles);
  const canResolveRole = Boolean(me) && Array.isArray(roles) && roles.length > 0;

  useEffect(() => {
    if (!storageID || !isAdmin) return;
    dispatch(fetchStorageById(storageID));
    dispatch(fetchAssets({ storageId: storageID, pageLimit: 1000 }));
  }, [dispatch, storageID, isAdmin]);

  const { running, view, error, start, stop, pollCount } = useSensorHealthCheck(storageID, assets);

  const [activeFilter, setActiveFilter] = useState<TriageBucket | null>(null);
  const [selectedKey, setSelectedKey] = useState<SensorKey | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const allSensors = useMemo(() => view.boards.flatMap((board) => board.sensors), [view]);
  const selectedSensor = useMemo(
    () => allSensors.find((sensor) => sensor.key === selectedKey) ?? null,
    [allSensors, selectedKey],
  );
  const overall = useMemo(() => deriveOverallStatus(view, view.boards.length), [view]);

  const highlightKeys = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    return new Set(
      allSensors
        .filter((sensor) => sensor.linkedAsset && assetLabel(sensor.linkedAsset).toLowerCase().includes(q))
        .map((sensor) => sensor.key),
    );
  }, [allSensors, searchQuery]);

  const visibleBoards = useMemo(() => {
    if (!activeFilter) return view.boards;
    return view.boards
      .map((board) => ({ ...board, sensors: board.sensors.filter((sensor) => sensor.bucket === activeFilter) }))
      .filter((board) => board.sensors.length > 0);
  }, [view.boards, activeFilter]);

  if (!canResolveRole) {
    return <div className="flex h-screen items-center justify-center text-sm text-gray-500">Loading…</div>;
  }
  if (!isAdmin) {
    return <Navigate replace to="/restricted" />;
  }

  const name = storage?.name || storageID;
  const online = Boolean(storage?.online);
  const hasChecked = view.totalSensors > 0 || running;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3.5 sm:px-6">
          <div className="flex items-center gap-3">
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${online ? "bg-green-500" : "bg-red-400"}`}
              aria-hidden
            />
            <div>
              <h1 className="text-lg font-semibold leading-tight text-gray-900">{name}</h1>
              <p className="text-xs text-gray-400">Sensor health · {online ? "Online" : "Offline"}</p>
            </div>
          </div>
          {running ? (
            <SecondaryButton icon="/images/icons/loading.svg" onClick={stop}>
              Stop check
            </SecondaryButton>
          ) : (
            <PrimaryButton onClick={start} disabled={!online}>
              Start check
            </PrimaryButton>
          )}
        </div>
        {error && (
          <div className="mx-auto max-w-6xl px-4 pb-2 sm:px-6">
            <p className="text-xs text-red-500">{error}</p>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-6xl space-y-5 px-4 py-6 sm:px-6">
        {!hasChecked ? (
          <SensorHealthEmptyState online={online} onStart={start} />
        ) : (
          <>
            <SensorHealthOverview
              status={overall}
              counts={view.counts}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              live={running}
              pollCount={pollCount}
            />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <SensorToolSearch
                sensors={allSensors}
                onSelectSensor={(sensor) => setSelectedKey(sensor.key)}
                onQueryChange={setSearchQuery}
              />
              {activeFilter && (
                <button
                  type="button"
                  onClick={() => setActiveFilter(null)}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Clear filter
                </button>
              )}
            </div>

            {visibleBoards.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                {activeFilter ? "No sensors match the current filter." : "Waiting for boards to report…"}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {visibleBoards.map((board) => (
                  <SensorBoardPanel
                    key={board.boardId}
                    boardId={board.boardId}
                    responding={board.responding}
                    sensors={board.sensors}
                    dimmedKeys={highlightKeys ?? undefined}
                    onSelectSensor={(sensor) => setSelectedKey(sensor.key)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {selectedSensor && (
        <SensorDetailModal sensor={selectedSensor} onClose={() => setSelectedKey(null)} running={running} />
      )}
    </div>
  );
};
